import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const RESEND_COOLDOWN = 60;

export default function OtpVerificationPage() {
  const navigate = useNavigate();
  const { verifyOtp, isAuthenticated } = useAuth();

  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem('otpSessionId'));
  const [email, setEmail] = useState(() => sessionStorage.getItem('otpEmail'));
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!sessionId) {
      navigate('/login');
      return;
    }
    inputRefs.current[0]?.focus();
  }, [sessionId, navigate]);

  // Navigate after auth state is committed — fixes React async state timing
  useEffect(() => {
    if (verified && isAuthenticated) {
      sessionStorage.removeItem('otpSessionId');
      sessionStorage.removeItem('otpEmail');
      navigate('/dashboard');
    }
  }, [verified, isAuthenticated, navigate]);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const handleDigit = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    setError(null);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length !== 6) { setError('Please enter all 6 digits.'); return; }
    if (!sessionId) { navigate('/login'); return; }

    setIsLoading(true);
    setError(null);
    try {
      await verifyOtp(sessionId, code);
      setVerified(true);
    } catch (err: any) {
      setError(err?.message ?? 'Invalid or expired OTP. Please try again.');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!sessionId || cooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendMsg(null);
    setError(null);
    try {
      const result = await authService.resendOtp(sessionId);
      setSessionId(result.otpSessionId);
      sessionStorage.setItem('otpSessionId', result.otpSessionId);
      if (result.email) {
        setEmail(result.email);
        sessionStorage.setItem('otpEmail', result.email);
      }
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setResendMsg('A new code has been sent.');
      startCooldown();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to resend OTP. Please try logging in again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-100 px-4">
      <div className="bg-white rounded-lg shadow-panel border border-ink-200 p-6 sm:p-8 w-full max-w-md">

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 bg-brand-900 rounded-md flex items-center justify-center shadow-soft border-b-2 border-accent-500">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-extrabold text-center text-ink-900 mb-1">Two-Factor Verification</h1>
        <p className="text-sm text-center text-ink-500 mb-1">Enter the 6-digit security code for</p>
        <p className="text-sm text-center font-bold text-brand-700 mb-6">{email ?? 'your account'}</p>

        <form onSubmit={handleSubmit}>
          <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleDigit(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className={`w-11 h-14 text-center text-xl font-bold border-2 rounded-xl outline-none transition-colors
                  ${error ? 'border-red-400 bg-red-50' : d ? 'border-brand-500 bg-brand-50' : 'border-gray-300 focus:border-brand-400'}`}
              />
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {resendMsg && !error && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-green-700">{resendMsg}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || digits.join('').length !== 6}
            className="w-full py-3 px-4 bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold rounded-xl shadow hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Verifying...
              </span>
            ) : 'Verify Code'}
          </button>
        </form>

        {/* Resend OTP */}
        <div className="mt-5 text-center">
          <p className="text-sm text-gray-500 mb-1">Didn't receive a code?</p>
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || resendLoading}
            className="text-sm font-medium text-brand-600 hover:text-brand-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {resendLoading ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
          </button>
        </div>

        <p className="text-center mt-4">
          <button
            onClick={() => { sessionStorage.removeItem('otpSessionId'); sessionStorage.removeItem('otpEmail'); navigate('/login'); }}
            className="text-sm text-gray-400 hover:text-brand-600 transition-colors"
          >
            ← Back to Login
          </button>
        </p>
      </div>
    </div>
  );
}
